// Editor Mode's tuning: every threshold and word list behind the habit
// lenses and revision passes, gathered here so they can be adjusted by trial
// and error without touching the detectors. See docs/editor-mode.md.
//
// Lists are matched case-insensitively. Multi-word entries match as phrases.

// ── Word echoes ──────────────────────────────────────────────────────────
// The same word (or root: walk / walked / walking) used twice too close
// together. How close is too close depends on how common the word is, ranked
// by src/data/word-frequency.txt (1 = most common). A word not in the list is
// rarer than all of them.

/** Words more common than this rank are never echoes. */
export const ECHO_MIN_RANK = 150

/** Words shorter than this are never echoes. */
export const ECHO_MIN_LETTERS = 3

/**
 * Echo distance, in words, by how common the word is. The first tier whose
 * maxRank covers the word applies; Infinity as a distance means anywhere on
 * the page.
 */
export const ECHO_TIERS: { maxRank: number; distance: number }[] = [
  { maxRank: 2500, distance: 40 }, // everyday words: door, hand, looked
  { maxRank: 12000, distance: 200 }, // less common: shrugged, gleaming
  { maxRank: Infinity, distance: Infinity }, // distinctive: gossamer, cerulean
]

/**
 * Never echoes, on top of everything above ECHO_MIN_RANK. Words a writer
 * repeats on purpose or that readers don't notice repeated.
 */
export const ECHO_IGNORE = [
  'said', 'says', 'say', 'asked', 'asks', 'ask', 'replied', 'told', 'tell',
  'yes', 'yeah', 'okay', 'ok', 'hey', 'oh', 'mr', 'mrs', 'ms', 'dr',
  'one', 'two', 'three', 'first', 'second', 'last', 'next',
  'something', 'nothing', 'anything', 'everything', 'someone', 'anyone', 'everyone',
  'back', 'around', 'away', 'again', 'still', 'even', 'just', 'like', 'into', 'onto', 'through',
  'mom', 'dad', 'mum',
]

// ── Filter words ─────────────────────────────────────────────────────────
// Perception and thought verbs that put a layer between the reader and the
// point-of-view character: "She saw the door open" rather than "The door
// opened". Flagged only after a subject (a pronoun or a character's name),
// optionally with helpers or one adverb between: "she could hear",
// "Mara suddenly realized", "he'd noticed".

/** Subjects, besides character names found on the page. */
export const FILTER_SUBJECTS = ['i', 'he', 'she', 'they', 'we', 'you', 'it']

/** Words that may stand between the subject and the verb (at most two). */
export const FILTER_HELPERS = [
  'could', "couldn't", 'can', "can't", 'cannot', 'would', "wouldn't", 'will', 'might', 'must', 'should',
  'did', "didn't", 'does', "doesn't", 'do', "don't", 'had', "hadn't", 'has', 'have', 'not', 'never',
  'just', 'still', 'already', 'finally', 'barely', 'almost', 'only', 'even', 'then', 'also', 'now',
  'suddenly', 'slowly', 'quickly', 'immediately', 'instantly', 'gradually', 'vaguely', 'dimly', 'clearly',
  'began to', 'started to', 'seemed to', 'tried to', 'managed to', 'continued to',
]

/** The perception and thought verbs, every form spelled out. */
export const FILTER_VERBS = [
  // seeing
  'see', 'sees', 'saw', 'seen', 'seeing',
  'watch', 'watches', 'watched', 'watching',
  'look', 'looks', 'looked', 'looking',
  'notice', 'notices', 'noticed', 'noticing',
  'observe', 'observes', 'observed', 'observing',
  'spot', 'spots', 'spotted', 'spotting',
  'glimpse', 'glimpses', 'glimpsed', 'glimpsing',
  'witness', 'witnesses', 'witnessed', 'witnessing',
  'perceive', 'perceives', 'perceived', 'perceiving',
  'detect', 'detects', 'detected', 'detecting',
  'register', 'registers', 'registered', 'registering',
  // hearing, touch, smell, taste
  'hear', 'hears', 'heard', 'hearing',
  'listen', 'listens', 'listened', 'listening',
  'feel', 'feels', 'felt', 'feeling',
  'sense', 'senses', 'sensed', 'sensing',
  'smell', 'smells', 'smelled', 'smelt', 'smelling',
  'taste', 'tastes', 'tasted', 'tasting',
  'experience', 'experiences', 'experienced', 'experiencing',
  // knowing and thinking
  'realize', 'realizes', 'realized', 'realizing', 'realise', 'realises', 'realised', 'realising',
  'recognize', 'recognizes', 'recognized', 'recognizing', 'recognise', 'recognises', 'recognised', 'recognising',
  'know', 'knows', 'knew', 'known', 'knowing',
  'understand', 'understands', 'understood', 'understanding',
  'learn', 'learns', 'learned', 'learnt', 'learning',
  'discover', 'discovers', 'discovered', 'discovering',
  'find', 'finds', 'found', 'finding',
  'note', 'notes', 'noted', 'noting',
  'wonder', 'wonders', 'wondered', 'wondering',
  'think', 'thinks', 'thought', 'thinking',
  'believe', 'believes', 'believed', 'believing',
  'consider', 'considers', 'considered', 'considering',
  'decide', 'decides', 'decided', 'deciding',
  'remember', 'remembers', 'remembered', 'remembering',
  'recall', 'recalls', 'recalled', 'recalling',
  'imagine', 'imagines', 'imagined', 'imagining',
  'suppose', 'supposes', 'supposed', 'supposing',
  'suspect', 'suspects', 'suspected', 'suspecting',
  'guess', 'guesses', 'guessed', 'guessing',
  'figure', 'figures', 'figured', 'figuring',
  // seeming
  'seem', 'seems', 'seemed', 'seeming',
  'appear', 'appears', 'appeared', 'appearing',
]

/** Verbs that are filter words only after a helper: "she could tell", not "she told him". */
export const FILTER_VERBS_AFTER_HELPER = [
  'tell', 'make out', 'catch sight', 'pick out', 'pick up on',
]

// ── Crutch words ─────────────────────────────────────────────────────────
// Words and phrases a draft leans on. The writer's own list starts as
// CRUTCH_DEFAULTS and is edited in the Habits tab; CRUTCH_SUGGESTIONS are
// offered there as one-click additions.

export const CRUTCH_DEFAULTS = [
  'just', 'really', 'very', 'quite', 'rather', 'somewhat', 'actually', 'suddenly',
  'began to', 'started to', 'a bit', 'a little',
]

export const CRUTCH_SUGGESTIONS = [
  // intensifiers and hedges
  'that', 'basically', 'literally', 'simply', 'totally', 'completely', 'absolutely', 'definitely',
  'certainly', 'honestly', 'truly', 'slightly', 'almost', 'nearly', 'perhaps', 'maybe', 'probably',
  'seemingly', 'kind of', 'sort of', 'a lot', 'pretty much', 'all of a sudden', 'in order to',
  'at that moment', 'for a moment', 'for a second', 'the fact that', 'managed to', 'proceeded to',
  'decided to', "couldn't help but", 'as if', 'as though', 'there was', 'there were',
  // stock body language
  'nodded', 'shrugged', 'smiled', 'grinned', 'sighed', 'glanced', 'frowned', 'blinked', 'swallowed',
  'turned', 'gazed', 'stared', 'let out a breath', 'rolled her eyes', 'rolled his eyes',
  'raised an eyebrow', 'bit her lip', 'bit his lip', 'heart pounded', 'heart raced', 'stomach dropped',
]

// ── Dialogue tags ────────────────────────────────────────────────────────

/**
 * Said-bookisms: verbs standing in for "said" in a dialogue tag. Flagged only
 * when used as the tag next to a quotation ("Get out," she hissed), never in
 * plain narration.
 */
export const SAID_BOOKISMS = [
  'hissed', 'growled', 'snarled', 'barked', 'spat', 'snapped', 'retorted', 'exclaimed', 'ejaculated',
  'chuckled', 'laughed', 'giggled', 'snickered', 'chortled', 'smiled', 'grinned', 'sneered', 'smirked',
  'sighed', 'breathed', 'gasped', 'gushed', 'purred', 'cooed', 'crooned', 'chirped', 'trilled',
  'squeaked', 'squealed', 'shrieked', 'screeched', 'bellowed', 'roared', 'thundered', 'boomed',
  'howled', 'wailed', 'whimpered', 'sobbed', 'blubbered', 'moaned', 'groaned', 'grunted', 'grumbled',
  'huffed', 'puffed', 'panted', 'wheezed', 'croaked', 'rasped', 'intoned', 'declared', 'proclaimed',
  'announced', 'pronounced', 'stated', 'asserted', 'opined', 'mused', 'pondered', 'reasoned',
  'theorized', 'speculated', 'surmised', 'interjected', 'interrupted', 'chimed', 'quipped', 'joked',
  'teased', 'jested', 'mocked', 'taunted', 'scoffed', 'jeered', 'admonished', 'chided', 'scolded',
  'berated', 'lectured', 'reprimanded', 'implored', 'begged', 'pleaded', 'beseeched', 'entreated',
  'urged', 'coaxed', 'cajoled', 'wheedled', 'demanded', 'ordered', 'commanded', 'instructed',
  'insisted', 'persisted', 'pressed', 'prodded', 'queried', 'inquired', 'enquired', 'questioned',
  'probed', 'ventured', 'offered', 'suggested', 'proposed', 'conceded', 'admitted', 'confessed',
  'acknowledged', 'allowed', 'agreed', 'concurred', 'affirmed', 'confirmed', 'assured', 'reassured',
  'soothed', 'consoled', 'comforted', 'encouraged', 'cautioned', 'warned', 'threatened', 'blurted',
  'stammered', 'stuttered', 'sputtered', 'spluttered', 'babbled', 'prattled', 'rambled', 'gabbled',
  'mumbled', 'muttered', 'murmured', 'whispered', 'uttered', 'voiced', 'vocalized', 'articulated',
  'enunciated', 'drawled', 'lisped', 'slurred', 'sang', 'hummed', 'cried', 'yelled', 'shouted',
  'screamed', 'called', 'hollered', 'exploded', 'erupted', 'fumed', 'seethed', 'raged', 'ranted',
  'raved', 'objected', 'protested', 'countered', 'rebutted', 'refuted', 'contradicted', 'argued',
  'disagreed', 'corrected', 'explained', 'elaborated', 'clarified', 'repeated', 'echoed', 'recited',
  'reported', 'related', 'recounted', 'revealed', 'disclosed', 'divulged', 'observed', 'noted',
  'remarked', 'commented', 'mentioned', 'concluded', 'gulped', 'swallowed', 'nodded', 'shrugged',
  'winked', 'beamed', 'grimaced', 'frowned', 'scowled', 'glowered', 'pouted',
]

/** Plain tags: never flagged as bookisms, but an adverb on them still is. */
export const NEUTRAL_TAGS = ['said', 'says', 'asked', 'asks', 'replied', 'answered', 'told', 'added', 'continued', 'went on']

/** A run of this many dialogue paragraphs in a row with no tag or action beat is flagged. */
export const UNTAGGED_RUN_LENGTH = 6

/** Narration of at least this many words beside a quotation counts as a beat that anchors the speaker. */
export const BEAT_MIN_WORDS = 2

// ── Paragraph walls ──────────────────────────────────────────────────────

/** Paragraphs of at least this many words are walls. */
export const PARAGRAPH_WALL_WORDS = 150

// ── Revision passes ──────────────────────────────────────────────────────

/**
 * A finished pass shows "changed since" once the page has been edited outside
 * Editor Mode this much: the share of paragraphs that are new or rewritten.
 */
export const PASS_CHANGED_SHARE = 0.15

// ── Dismissals ───────────────────────────────────────────────────────────

/** "Ignore here" entries kept per page; the oldest go first. */
export const IGNORES_PER_PAGE = 300
